import { useEffect, useRef } from 'react';
import {
  Target,
  Calendar,
  TrendingUp,
  Award,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import { Button } from '../ui/button';

// Define available sections
const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: Target },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'training', label: 'Training', icon: TrendingUp },
  { id: 'journey', label: 'Journey', icon: Award },
];

const ProgressSectionNav = ({ studentId, activeSection, onSectionChange, children, hideNav = false, isSectionVisible = () => true }) => {
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndX = useRef(0);
  const touchEndY = useRef(0);
  const isSwiping = useRef(false);

  // Filter sections by portal visibility
  const filteredSections = SECTIONS.filter(s => isSectionVisible(`progress.${s.id}`));

  // Load saved section from localStorage on mount
  useEffect(() => {
    if (studentId) {
      const savedSection = localStorage.getItem(`progress_section_${studentId}`);
      if (savedSection && filteredSections.find(s => s.id === savedSection)) {
        onSectionChange(savedSection);
      } else if (filteredSections.length > 0 && !filteredSections.find(s => s.id === activeSection)) {
        // Current section not visible — redirect to first visible
        onSectionChange(filteredSections[0].id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, filteredSections.length]);

  // Save section to localStorage when it changes
  useEffect(() => {
    if (studentId && activeSection) {
      localStorage.setItem(`progress_section_${studentId}`, activeSection);
    }
  }, [studentId, activeSection]);

  // Get current section index within filtered list
  const currentIndex = filteredSections.findIndex(s => s.id === activeSection);

  // Navigation handlers
  const goToPrevSection = () => {
    if (currentIndex > 0) {
      onSectionChange(filteredSections[currentIndex - 1].id);
    }
  };

  const goToNextSection = () => {
    if (currentIndex < filteredSections.length - 1) {
      onSectionChange(filteredSections[currentIndex + 1].id);
    }
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    // Reset end positions to start positions (prevents false swipes on tap)
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
    isSwiping.current = false;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;

    // Check if this is a horizontal swipe (more horizontal than vertical movement)
    const deltaX = Math.abs(touchEndX.current - touchStartX.current);
    const deltaY = Math.abs(touchEndY.current - touchStartY.current);

    // Only consider it a swipe if horizontal movement is significant and greater than vertical
    if (deltaX > 20 && deltaX > deltaY) {
      isSwiping.current = true;
    }
  };

  const handleTouchEnd = () => {
    // Only process swipe if we detected horizontal swipe movement
    if (!isSwiping.current) {
      return;
    }

    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 75; // Increased threshold for more intentional swipes

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swiped left - go to next
        goToNextSection();
      } else {
        // Swiped right - go to previous
        goToPrevSection();
      }
    }

    // Reset swipe state
    isSwiping.current = false;
  };

  return (
    <div className={hideNav ? '' : 'space-y-4'}>
      {/* Section Navigation Bar - hidden when hideNav is true */}
      {!hideNav && (
        <>
          <div className="flex items-center gap-2">
            {/* Previous Arrow (desktop) */}
            <Button
              type="button"
              variant="memberSecondary"
              size="sm"
              onClick={goToPrevSection}
              disabled={currentIndex === 0}
              className="hidden sm:flex h-9 w-9 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {/* Section Pills */}
            <div className="flex-1 flex items-center justify-center gap-1 sm:gap-2 overflow-x-auto">
              {filteredSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => onSectionChange(section.id)}
                    className={`
                      flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium
                      transition-all duration-200 whitespace-nowrap
                      ${isActive
                        ? 'bg-[#9B1B30] text-white shadow-md'
                        : 'bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-600'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{section.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Next Arrow (desktop) */}
            <Button
              type="button"
              variant="memberSecondary"
              size="sm"
              onClick={goToNextSection}
              disabled={currentIndex === filteredSections.length - 1}
              className="hidden sm:flex h-9 w-9 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Section Indicator Dots (mobile) */}
          <div className="flex justify-center gap-1.5 sm:hidden">
            {filteredSections.map((section, idx) => (
              <div
                key={section.id}
                className={`
                  h-1.5 rounded-full transition-all duration-200
                  ${idx === currentIndex ? 'w-4 bg-[#9B1B30]' : 'w-1.5 bg-stone-300 dark:bg-stone-600'}
                `}
              />
            ))}
          </div>
        </>
      )}

      {/* Content Area with Swipe Support */}
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="min-h-[300px] transition-opacity duration-200"
      >
        {children}
      </div>
    </div>
  );
};

export default ProgressSectionNav;
export { SECTIONS };
